import React, {useCallback, useRef, useState} from "react";
import {SelectBox} from "devextreme-react";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";
import useProjectOrganisationContext from "@/pages/takeoff/context/project-organisation/use-project-organisation-context";
import useGetStoreyTypes from "@/hooks/use-get-storey-types.ts";
import { StoreyTypologyType } from "@/models/types/storey-typology-type";
import ClosableDialog from "@/components/dialogs/closable-dialog";
import Button from "@/components/buttons/button";
import { BaseDialogProps } from "@/components/dialogs/base-dialog";

interface StoreyFormElements extends HTMLFormControlsCollection {
    storeyName: HTMLInputElement,
    storeyAltimetry: HTMLInputElement,
    storeyType: HTMLInputElement,
}

interface FormElement extends HTMLFormElement {
    readonly elements: StoreyFormElements
}

export interface AddOrEditStoreyDialogProps extends BaseDialogProps {
    buildingId?: string;
    storeyId?: string;
}

const AddOrEditStoreyDialog: React.FC<AddOrEditStoreyDialogProps> = ({buildingId, storeyId, open, onClose }) => {
    const { canEdit, ensureCanEdit } = useProjectEditAccess();
    const {data, tryAddStorey, tryEditStorey} = useProjectOrganisationContext();
    const storeyTypes = useGetStoreyTypes();
    
    const [error, setError] = useState<string | undefined>(undefined);
    
    const nameInputRef = useRef(null);
    
    let cachedName;
    let cachedAltimetry;
    let cachedStoreyType;
    const isEditing = buildingId != null && storeyId != null;
    if(isEditing) {
        const storey = data?.getStoreyBuilding(buildingId, storeyId);
        if(storey) {
            cachedName = storey.name;
            cachedAltimetry = storey.altimetry;
            cachedStoreyType = storey.storeyType;
        }
    } else {
        cachedName = ''
        cachedAltimetry = 0;
        cachedStoreyType = 'SUPER';
    }
    
    const closeDialog = function () {
        onClose(true);
        setError(undefined);
    }
    
    const handleAltimetryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const { value } = input;

        // Autoriser vide sans erreur pendant la saisie
        if (value === '') {
            setError(undefined);
            return;
        }

        // Etats transitoires tolérés pendant la saisie:
        // 1) Signe négatif seul
        if (value === '-') {
            setError(undefined);
            return;
        }
    
        // 2) Entier (avec ou sans signe) suivi d'un séparateur (ex: "12," | "-3.")
        if (/^-?\d+[.,]$/.test(value)) {
            setError(undefined);
            return;
        }

        // Validation standard basée sur le pattern de l'input
        const regex = new RegExp(input.pattern);
        if (!regex.test(value)) {
            setError("L'altimétrie doit être un nombre avec jusqu'à trois décimales.");
        } else {
            setError(undefined);
        }
    }, []);

    const handleSubmit = function(event: React.FormEvent<FormElement>) {
        event.preventDefault()
        if (!ensureCanEdit()) {
            return;
        }

        const {elements} = event.currentTarget;
        const storeyName = elements.storeyName.value.trim();
        
        if(storeyName == null || storeyName === '') {
            setError("Veuillez renseigner un nom");
            return;
        }
        
        let storeyAltimetry;
        if (elements.storeyAltimetry.value !== "") {
            const raw = elements.storeyAltimetry.value.trim();
            
            const {pattern} = (elements.storeyAltimetry as HTMLInputElement);
            const regex = new RegExp(pattern);
            if (!regex.test(raw)) {
                setError("L'altimétrie doit être un nombre avec jusqu'à trois décimales.");
                return;
            }
            
            // Remplacer ',' par '.' parce que youpi les USA.
            const normalized = raw.replace(/,/g, ".");
            const parsed = Number(normalized);
            if (Number.isNaN(parsed)) {
                setError("L'altimétrie est invalide.");
                return;
            }
            storeyAltimetry = parsed;
        }
        
        let storeyType;
        if(elements.storeyType.value !== '') {
            storeyType = elements.storeyType.value.trim();
        }
        
        if(buildingId == null)
            return;
        
        if(isEditing) {
            try {
                tryEditStorey(buildingId, storeyId, storeyName, storeyAltimetry, storeyType as StoreyTypologyType);
                closeDialog();
            } catch(e: unknown) {
                if(e instanceof Error) {
                    setError(e.message);
                }
            }
        } else {
            if(storeyName == null){
                setError('Veuillez renseigner un nom.');
                return;
            }
            
            if(storeyAltimetry == null){
                setError('Veuillez renseigner une altimétrie');
                return;
            }
            
            try {
                tryAddStorey(buildingId, storeyName, storeyAltimetry, storeyType as StoreyTypologyType ?? 'SUPER');
                closeDialog();
            } catch (e: unknown) {
                if(e instanceof Error) {
                    setError(e.message);
                }
            }
        }
    }

    return (
        <ClosableDialog open={open} onClose={closeDialog} title={isEditing ? 'Modifier l\'étage' : 'Nouvel étage'}>
            <form onSubmit={handleSubmit} className='flex flex-col gap-y-4 pt-2'>
                {error && <div className='bg-red-400 rounded-sm p-2 text-white'>{error}</div>}

                <div className='grid grid-cols-[auto_1fr] grid-rows-auto gap-x-6 gap-y-4 items-center'>
                    <label>Nom</label>

                    <input disabled={!canEdit}
                        ref={nameInputRef}
                        autoFocus={true}
                        tabIndex={1}
                        id="storey-name"
                        name="storeyName"
                        type="text"
                        defaultValue={cachedName}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                    />

                    <label className="text-sm/6 font-medium text-gray-900">Altimétrie (m)</label>

                    <input disabled={!canEdit}
                        tabIndex={2}
                        id="storey-altimetry"
                        name="storeyAltimetry"
                        type="text" inputMode="decimal" pattern="^-?\d+([.,]\d{1,3})?$"
                        onChange={handleAltimetryChange}
                        defaultValue={String(cachedAltimetry)}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                    />

                    <label className="text-sm/6 font-medium text-gray-900">Type d'étage</label>

                    <SelectBox dataSource={storeyTypes} name={'storeyType'} defaultValue={cachedStoreyType}
                               id='storey-type' tabIndex={4} height={36}/>
                </div>

                <div className='flex flex-row gap-x-2 justify-end'>
                    <Button disabled={!canEdit} type='submit'>
                        {isEditing ? 'Modifier' : 'Créer'}
                    </Button>
                </div>
            </form>
        </ClosableDialog>
    )
}

export default AddOrEditStoreyDialog;